import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * D-pad friendly transport row: play/pause, mute and (for video) fullscreen
 * as plain focusable buttons.
 *
 * TV renders this instead of the browser's native media controls — on an
 * Android TV WebView the D-pad can focus the <video>/<audio> element but
 * never reaches the buttons inside its controls, so OK did nothing.
 *
 * While `fullscreenTarget` is fullscreen this row is off screen, so the
 * remote is handled here directly: OK toggles play/pause, up/down zap and
 * Back/Escape leaves fullscreen (Back is wired in useBackButton).
 */
export function PlayerControls({
  media,
  paused,
  onTogglePause,
  fullscreenTarget,
  onZap,
}: {
  /** The live <video>/<audio>; changes whenever the player switches URL. */
  media: HTMLMediaElement | null;
  paused: boolean;
  onTogglePause: () => void;
  /** Element to send fullscreen. Omit for audio. */
  fullscreenTarget?: React.RefObject<HTMLElement | null>;
  onZap?: (dir: 1 | -1) => void;
}) {
  const { t } = useTranslation();
  const [muted, setMuted] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const fullscreenButton = useRef<HTMLButtonElement>(null);
  const handlers = useRef({ onTogglePause, onZap });
  handlers.current = { onTogglePause, onZap };

  // Mute is a property of the element, so carry it over when the player
  // swaps to a fallback URL and mirror changes made elsewhere.
  useEffect(() => {
    if (!media) return;
    media.muted = muted;
    const sync = () => setMuted(media.muted);
    media.addEventListener("volumechange", sync);
    return () => media.removeEventListener("volumechange", sync);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [media]);

  useEffect(() => {
    const target = fullscreenTarget?.current;
    if (!target) return;
    const onChange = () => {
      const on = document.fullscreenElement === target;
      setFullscreen(on);
      // Land focus back on the button that opened it, not the top of the page.
      if (!on) fullscreenButton.current?.focus();
    };
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, [fullscreenTarget]);

  useEffect(() => {
    if (!fullscreen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        handlers.current.onTogglePause();
      } else if (e.key === "ArrowUp" && handlers.current.onZap) {
        e.preventDefault();
        handlers.current.onZap(1);
      } else if (e.key === "ArrowDown" && handlers.current.onZap) {
        e.preventDefault();
        handlers.current.onZap(-1);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [fullscreen]);

  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    if (media) media.muted = next;
  };

  const toggleFullscreen = () => {
    const target = fullscreenTarget?.current;
    if (!target) return;
    if (document.fullscreenElement) void document.exitFullscreen().catch(() => {});
    else void target.requestFullscreen?.().catch((err) => console.warn("[player] fullscreen failed:", err));
  };

  const cls =
    "h-10 min-w-10 px-3 inline-flex items-center justify-center gap-2 rounded border border-border " +
    "text-text/80 hover:border-accent hover:text-text focus-visible:border-accent focus-visible:text-text " +
    "tv:h-12 tv:min-w-12";

  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={onTogglePause}
        className={cls}
        aria-label={paused ? t("play") : t("pause")}
        title={paused ? t("play") : t("pause")}
      >
        <Icon d={paused ? "M7 4v16l13-8z" : "M7 4h4v16H7zM13 4h4v16h-4z"} filled />
      </button>
      <button
        type="button"
        onClick={toggleMute}
        className={cls}
        aria-pressed={muted}
        aria-label={muted ? t("unmute") : t("mute")}
        title={muted ? t("unmute") : t("mute")}
      >
        <Icon d={muted ? "M11 5 6 9H2v6h4l5 4zM23 9l-6 6M17 9l6 6" : "M11 5 6 9H2v6h4l5 4zM15.5 8.5a5 5 0 0 1 0 7M19 5a10 10 0 0 1 0 14"} />
      </button>
      {fullscreenTarget ? (
        <button
          ref={fullscreenButton}
          type="button"
          onClick={toggleFullscreen}
          className={cls}
          aria-label={t("fullscreen")}
          title={t("fullscreen")}
        >
          <Icon d="M4 9V4h5M15 4h5v5M20 15v5h-5M9 20H4v-5" />
        </button>
      ) : null}
    </div>
  );
}

function Icon({ d, filled = false }: { d: string; filled?: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="20"
      height="20"
      fill={filled ? "currentColor" : "none"}
      stroke={filled ? "none" : "currentColor"}
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d={d} />
    </svg>
  );
}
